import React from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";

/**
 * SalesOrderFormDialog — create/edit SO. Parent owns form + submit.
 */
export default function SalesOrderFormDialog({
  open,
  onOpenChange,
  editing,
  form,
  setForm,
  submitting,
  onSubmit,
  plants,
  products,
  districts,
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-lg max-w-lg">
        <DialogHeader>
          <DialogTitle>{editing ? "Edit SO" : "Tambah SO"}</DialogTitle>
        </DialogHeader>
        <form onSubmit={onSubmit} className="space-y-4" data-testid="so-form">
          <div>
            <Label className="overline block mb-2">No SO *</Label>
            <Input
              value={form.no_so}
              onChange={(e) => setForm((f) => ({ ...f, no_so: e.target.value }))}
              required
              className="rounded-lg h-11 font-mono"
              data-testid="input-so-no"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="overline block mb-2">Plant *</Label>
              <Select value={form.plant_id} onValueChange={(v) => setForm((f) => ({ ...f, plant_id: v }))} disabled={!!editing}>
                <SelectTrigger className="rounded-lg h-11" data-testid="input-so-plant"><SelectValue placeholder="Pilih plant" /></SelectTrigger>
                <SelectContent>
                  {plants.map((p) => <SelectItem key={p.id} value={p.id}>{p.kode} · {p.nama}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="overline block mb-2">Produk *</Label>
              <Select value={form.produk_id} onValueChange={(v) => setForm((f) => ({ ...f, produk_id: v }))} disabled={!!editing}>
                <SelectTrigger className="rounded-lg h-11" data-testid="input-so-produk"><SelectValue placeholder="Pilih produk" /></SelectTrigger>
                <SelectContent>
                  {products.map((p) => <SelectItem key={p.id} value={p.id}>{p.nama}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label className="overline block mb-2">Distrik (opsional)</Label>
            <Select
              value={form.distrik_id || "__any__"}
              onValueChange={(v) => setForm((f) => ({ ...f, distrik_id: v === "__any__" ? "" : v }))}
            >
              <SelectTrigger className="rounded-lg h-11" data-testid="input-so-distrik">
                <SelectValue placeholder="Semua distrik (default)" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__any__">— Semua distrik —</SelectItem>
                {districts.map((d) => (
                  <SelectItem key={d.id} value={d.id}>{d.kode} · {d.nama}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-[10px] text-muted-foreground mt-1">
              <strong>Wajib pilih distrik</strong> agar SO ini muncul di form SPB pada distrik yang sama. SO tanpa distrik tidak akan bisa dipakai untuk buat SPB.
            </p>
          </div>
          <div>
            <Label className="overline block mb-2">Kuantiti Total *</Label>
            <Input
              type="number"
              step="0.01"
              value={form.kuantiti_total}
              onChange={(e) => setForm((f) => ({ ...f, kuantiti_total: e.target.value }))}
              required
              className="rounded-lg h-11"
              data-testid="input-so-total"
            />
            <p className="text-[10px] text-muted-foreground mt-1">
              Ambang alert stok rendah kini diatur per <a href="/products" className="underline font-bold">Produk</a>, bukan per SO.
            </p>
          </div>
          <div>
            <Label className="overline block mb-2">Tanggal Terbit</Label>
            <Input
              type="date"
              value={form.tanggal_terbit}
              onChange={(e) => setForm((f) => ({ ...f, tanggal_terbit: e.target.value }))}
              className="rounded-lg h-11"
              data-testid="input-so-tanggal"
            />
          </div>
          <div className="flex items-center gap-2">
            <Switch checked={form.aktif} onCheckedChange={(v) => setForm((f) => ({ ...f, aktif: v }))} />
            <span className="text-sm">Aktif</span>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" className="rounded-lg" onClick={() => onOpenChange(false)}>Batal</Button>
            <Button type="submit" disabled={submitting} className="rounded-lg font-bold uppercase" data-testid="save-so-button">
              {submitting ? "Menyimpan..." : "Simpan"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
